import { useMemo, useRef } from 'react'
import type { RefObject } from 'react'
import { useReactFlow, useStore } from '@xyflow/react'
import {
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignVerticalDistributeCenter,
} from 'lucide-react'
import FloatingMenu from '../../components/shared/FloatingMenu'
import { ALIGNMENT_LABELS, boundingBox, MIN_DISTRIBUTE_BOXES } from './chartAlignment'
import type { AlignmentAction, AlignmentBox } from './chartAlignment'

/** Width and height the toolbar is laid out at, used to anchor it. */
export const SELECTION_TOOLBAR_WIDTH = 214
export const SELECTION_TOOLBAR_HEIGHT = 36

const TOOLS: { action: AlignmentAction; Icon: typeof AlignStartVertical; testId: string }[] = [
  { action: 'align-left', Icon: AlignStartVertical, testId: 'align-left' },
  { action: 'align-center', Icon: AlignCenterVertical, testId: 'align-center' },
  { action: 'align-right', Icon: AlignEndVertical, testId: 'align-right' },
  { action: 'distribute-horizontal', Icon: AlignHorizontalDistributeCenter, testId: 'distribute-horizontal' },
  { action: 'distribute-vertical', Icon: AlignVerticalDistributeCenter, testId: 'distribute-vertical' },
]

interface LayerProps {
  /** The selected boxes in chart coordinates, as they are currently drawn. */
  boxes: AlignmentBox[]
  /** The canvas the toolbar must stay inside. */
  paneRef: RefObject<HTMLDivElement>
  /** Hide the toolbar mid-gesture rather than let it chase the boxes. */
  hidden?: boolean
  busy: boolean
  onApply: (action: AlignmentAction) => void
}

/**
 * Keeps the alignment toolbar pinned over its selection as the camera moves.
 *
 * The camera is subscribed to HERE rather than in the page: a selection of
 * fewer than two boxes returns a constant, so panning and zooming an ordinary
 * chart re-renders nothing, and when there is a selection only this small
 * component repaints per frame.
 */
export function SelectionToolbarLayer({ boxes, paneRef, hidden, busy, onApply }: LayerProps) {
  const { flowToScreenPosition } = useReactFlow()
  const active = boxes.length >= 2 && !hidden
  const camera = useStore((state: any) => (
    active ? `${Math.round(state.transform[0])}:${Math.round(state.transform[1])}:${state.transform[2].toFixed(4)}` : ''
  ))

  const anchor = useMemo(() => {
    if (!active) return null
    const box = boundingBox(boxes)
    const pane = paneRef.current?.getBoundingClientRect()
    if (!box || !pane) return null
    void camera
    const topLeft = flowToScreenPosition({ x: box.x, y: box.y })
    const topRight = flowToScreenPosition({ x: box.right, y: box.y })
    const centre = (topLeft.x + topRight.x) / 2
    return {
      // Centred on the selection, but never pushed off the canvas: a selection
      // at the very top of the pane still has its controls within reach.
      left: Math.min(
        Math.max(centre - SELECTION_TOOLBAR_WIDTH / 2, pane.left + 8),
        Math.max(pane.right - SELECTION_TOOLBAR_WIDTH - 8, pane.left + 8),
      ),
      top: Math.max(topLeft.y - SELECTION_TOOLBAR_HEIGHT - 12, pane.top + 8),
    }
  }, [active, boxes, camera, flowToScreenPosition, paneRef])

  if (!anchor) return null
  return (
    <SelectionToolbar
      left={anchor.left}
      top={anchor.top}
      selectionSize={boxes.length}
      busy={busy}
      onApply={onApply}
    />
  )
}

interface Props {
  /** Where the toolbar sits, in viewport pixels: the top-left of its anchor. */
  left: number
  top: number
  selectionSize: number
  busy: boolean
  onApply: (action: AlignmentAction) => void
}

/**
 * The floating align/distribute strip that appears over a multi-box selection.
 *
 * It is positioned by the app's shared floating-menu component, against an
 * invisible anchor the caller has already placed over the selection. Outside
 * clicks and Escape deliberately do NOT close it: the toolbar is not a menu,
 * it belongs to the selection and goes away when the selection does.
 */
export default function SelectionToolbar({ left, top, selectionSize, busy, onApply }: Props) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const canDistribute = selectionSize >= MIN_DISTRIBUTE_BOXES

  return (
    <>
      <div
        ref={anchorRef}
        aria-hidden="true"
        style={{ position: 'fixed', left, top, width: SELECTION_TOOLBAR_WIDTH, height: 1, pointerEvents: 'none' }}
      />
      {/* Remounting on every measurable move is what keeps the strip pinned to
          the selection while the camera pans and zooms: the shared menu only
          re-measures its anchor on scroll and resize. */}
      <FloatingMenu
        key={`${Math.round(left)}:${Math.round(top)}`}
        open
        onClose={() => {}}
        closeOnEscape={false}
        anchorRef={anchorRef}
        width={SELECTION_TOOLBAR_WIDTH}
        align="start"
        gap={4}
        className="bg-white border border-stone rounded-card shadow-elevated px-1.5 py-1 flex items-center gap-0.5"
        style={{ width: SELECTION_TOOLBAR_WIDTH }}
      >
        <div
          role="toolbar"
          aria-label="Align selected roles"
          data-testid="selection-toolbar"
          className="flex items-center gap-0.5"
        >
          {TOOLS.map(({ action, Icon, testId }) => {
            const distributing = action.startsWith('distribute')
            const disabled = busy || (distributing && !canDistribute)
            return (
              <button
                key={action}
                type="button"
                data-testid={`selection-${testId}`}
                disabled={disabled}
                title={
                  distributing && !canDistribute
                    ? `Select at least ${MIN_DISTRIBUTE_BOXES} to ${ALIGNMENT_LABELS[action]}`
                    : ALIGNMENT_LABELS[action].replace(/^./, c => c.toUpperCase())
                }
                aria-label={ALIGNMENT_LABELS[action]}
                onClick={() => onApply(action)}
                className="p-1.5 rounded-button text-text-primary hover:bg-stone/40 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <Icon size={16} aria-hidden="true" />
              </button>
            )
          })}
          <span className="pl-1.5 pr-1 text-xs text-text-secondary whitespace-nowrap" data-testid="selection-count">
            {selectionSize} selected
          </span>
        </div>
      </FloatingMenu>
    </>
  )
}
